import React from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, RefreshCw, ExternalLink, Search, Link2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function ChartMogulPanel({ account, reload }) {
  const [health, setHealth] = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [query, setQuery] = React.useState(account.name || "");
  const [matching, setMatching] = React.useState(false);
  const [matches, setMatches] = React.useState(null);
  const [manualUuid, setManualUuid] = React.useState(account.chartmogul_uuid || "");

  const load = React.useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/accounts/${account.id}/chartmogul/metrics${refresh ? "?refresh=true" : ""}`);
      setMetrics(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load metrics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [account.id]);

  React.useEffect(() => {
    (async () => {
      try {
        const h = await api.get("/chartmogul/health");
        setHealth(h.data);
      } catch (e) {
        setHealth({ connected: false, error: "Health check failed" });
      }
      if (account.chartmogul_uuid) await load(false);
    })();
  }, [account.chartmogul_uuid, load]);

  const runMatch = async () => {
    setMatching(true);
    setMatches(null);
    setError(null);
    try {
      const r = await api.post(`/accounts/${account.id}/chartmogul/match`, { query });
      setMatches(r.data.matches);
    } catch (e) {
      setError(e?.response?.data?.detail || "Match failed");
    } finally {
      setMatching(false);
    }
  };

  const bind = async (uuid) => {
    try {
      await api.post(`/accounts/${account.id}/chartmogul/bind`, { chartmogul_uuid: uuid });
      reload && reload();
      await load(true);
      setMatches(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Bind failed");
    }
  };

  const unlink = async () => {
    if (!window.confirm("Unlink this account from ChartMogul?")) return;
    await api.post(`/accounts/${account.id}/chartmogul/bind`, { chartmogul_uuid: "" });
    reload && reload();
    setMetrics(null);
  };

  return (
    <div className="space-y-6" data-testid="chartmogul-panel">
      {/* Connection status */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-md flex items-center justify-center ${health?.connected ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
              <BarChart3 className="h-4 w-4" />
            </div>
            <div>
              <div className="font-heading text-sm font-bold text-slate-900">ChartMogul integration</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {health?.connected
                  ? <span className="text-emerald-700">Connected · {health.key_type || "Read-only"}</span>
                  : <span className="text-red-600">Not connected{health?.error ? ` — ${health.error}` : ""}</span>}
              </div>
            </div>
          </div>
          {account.chartmogul_uuid && (
            <div className="flex gap-2">
              <button onClick={() => load(true)} disabled={refreshing}
                className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md hover:border-blue-400 flex items-center gap-1"
                data-testid="chartmogul-refresh-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh now
              </button>
              <button onClick={unlink}
                className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md hover:border-red-400 hover:text-red-600"
                data-testid="chartmogul-unlink-btn">
                Unlink
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Matcher (always available so you can re-link) */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-600" /> Link to ChartMogul customer
          </CardTitle>
          <div className="text-xs text-slate-500 mt-1">
            {account.chartmogul_uuid
              ? <>Currently linked to <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{account.chartmogul_uuid}</code></>
              : "Search ChartMogul by company name or paste a UUID directly."}
          </div>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="flex gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), runMatch())}
              placeholder="Search by company name…"
              className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="chartmogul-search-input"
            />
            <button onClick={runMatch} disabled={matching || !query.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="chartmogul-search-btn">
              <Search className="h-4 w-4" /> {matching ? "Searching…" : "Search"}
            </button>
          </div>

          {matches !== null && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              {matches.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 text-center">No customers matched "{query}".</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-3 py-2">Company</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">MRR</th>
                      <th className="px-3 py-2 text-right">Run-rate ARR</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m) => (
                      <tr key={m.uuid} className="border-b border-slate-100 last:border-b-0" data-testid={`chartmogul-match-${m.uuid}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{m.company || m.name || "—"}</div>
                          <div className="font-mono text-[10px] text-slate-400">{m.uuid}</div>
                        </td>
                        <td className="px-3 py-2"><StatusBadge value={m.status} /></td>
                        <td className="px-3 py-2 text-right font-mono">{fmtMoney(m.mrr)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtMoney(m.arr)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => bind(m.uuid)}
                            className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            data-testid={`chartmogul-bind-${m.uuid}`}>
                            Link
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-blue-700">Or paste a ChartMogul UUID directly</summary>
            <div className="mt-2 flex gap-2">
              <input value={manualUuid} onChange={(e) => setManualUuid(e.target.value)} placeholder="cus_..."
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                data-testid="chartmogul-uuid-input" />
              <button onClick={() => bind(manualUuid.trim())} disabled={!manualUuid.trim()}
                className="px-3 py-2 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50"
                data-testid="chartmogul-uuid-save">
                Save
              </button>
            </div>
          </details>

          {error && (
            <div className="text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{error}</div>
          )}
        </CardContent>
      </Card>

      {/* Live metrics */}
      {!account.chartmogul_uuid ? (
        <Card className="border-slate-200 p-12 text-center">
          <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">Not linked yet</div>
          <div className="text-sm text-slate-500 mt-1">Search above to find this customer in ChartMogul.</div>
        </Card>
      ) : loading ? (
        <div className="text-sm text-slate-500 font-mono">Loading ChartMogul…</div>
      ) : metrics?.linked ? (
        <MetricsView metrics={metrics} />
      ) : null}
    </div>
  );
}

function MetricsView({ metrics }) {
  const s = metrics.summary || {};
  const series = metrics.mrr_series || [];
  const max = Math.max(1, ...series.map((p) => p.mrr));
  return (
    <>
      <Card className="border-slate-200" data-testid="chartmogul-summary">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">ChartMogul customer</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-heading text-xl font-bold text-slate-900">{s.company || s.name || "—"}</div>
                <StatusBadge value={s.status} />
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                Since {fmtDate(s.customer_since)} · {s.country || "—"} · {s.currency || "GBP"}
              </div>
            </div>
            {s.chartmogul_url && (
              <a href={s.chartmogul_url} target="_blank" rel="noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                data-testid="chartmogul-open-link">
                Open in ChartMogul <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
            <Stat label="MRR" value={fmtMoney(s.mrr)} accent="emerald" />
            <Stat label="Run-rate ARR" value={fmtMoney(s.arr)} accent="blue" />
            <Stat label="Subscriptions" value={(metrics.subscriptions || []).filter((x) => x.status === "active").length} />
            <Stat label="Status" value={<StatusBadge value={s.status} />} />
          </div>
          <div className="mt-2 text-[11px] text-slate-400 font-mono">
            {metrics.cached ? "Cached · " : "Live · "}fetched {fmtDate(metrics.fetched_at)}
          </div>
        </CardContent>
      </Card>

      {series.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading">MRR movements</CardTitle>
            <div className="text-xs text-slate-500 mt-1">Running MRR based on ChartMogul activity events.</div>
          </CardHeader>
          <CardContent className="p-5">
            <div className="flex items-end gap-1 h-32">
              {series.slice(-24).map((p, i) => (
                <div key={i} className="flex-1 min-w-[8px] bg-blue-100 hover:bg-blue-300 rounded-t" title={`${p.date}: £${p.mrr}`}
                  style={{ height: `${(p.mrr / max) * 100}%` }} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(metrics.subscriptions || []).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading">Subscriptions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Plan</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">MRR</th>
                  <th className="px-4 py-2 text-right">Run-rate ARR</th>
                  <th className="px-4 py-2">Cycle</th>
                  <th className="px-4 py-2">Started</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(metrics.subscriptions || []).map((sub) => (
                  <tr key={sub.uuid} className="border-b border-slate-100" data-testid={`chartmogul-sub-${sub.uuid}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{sub.plan || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{sub.quantity}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtMoney(sub.mrr)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtMoney(sub.arr)}</td>
                    <td className="px-4 py-2 text-[12px] text-slate-600">{sub.billing_cycle || "—"}</td>
                    <td className="px-4 py-2 text-[12px] font-mono text-slate-500">{fmtDate(sub.start_date)}</td>
                    <td className="px-4 py-2"><StatusBadge value={sub.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(metrics.activities || []).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading">Recent activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">MRR Δ</th>
                </tr>
              </thead>
              <tbody>
                {(metrics.activities || []).map((a, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`chartmogul-activity-${i}`}>
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-500">{fmtDate(a.date)}</td>
                    <td className="px-4 py-2 text-[12px] text-slate-700">{a.type || "—"}</td>
                    <td className="px-4 py-2 text-[12px] text-slate-700">{a.description || "—"}</td>
                    <td className={`px-4 py-2 text-right font-mono ${a.mrr_movement > 0 ? "text-emerald-700" : a.mrr_movement < 0 ? "text-red-600" : "text-slate-500"}`}>
                      {a.mrr_movement ? fmtMoney(a.mrr_movement) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function Stat({ label, value, accent }) {
  const colour = accent === "emerald" ? "text-emerald-700" : accent === "blue" ? "text-blue-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${colour}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ value }) {
  if (!value) return <span className="text-slate-400 text-xs">—</span>;
  const palette = {
    Active: "bg-emerald-50 text-emerald-700 border-emerald-100",
    active: "bg-emerald-50 text-emerald-700 border-emerald-100",
    Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    Past_Due: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border ${palette[value] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {value}
    </span>
  );
}
